import type { User } from '@auth0/auth0-react';
import { jwtDecode } from 'jwt-decode';
import {
  AUTH0_LOCALHOST_ROPG_PATH,
  AUTH0_LOCALHOST_ROPG_SKIP_KEY,
  AUTH0_LOCALHOST_ROPG_TOKENS_KEY,
} from '@shared/constants';
import {
  ErrorMessage,
  errorStatusCodes,
} from '@shared/constants/server-errors.constants';
import { isLocalHostname } from '@shared/helpers/environment.helpers';
import { parseAuth0RopgTokenResponse } from '@shared/helpers/auth0-ropg.helpers';
import { guards } from '@shared/helpers/typeguards.helpers';
import { Auth0RopgTokens } from '@shared/models/auth0-ropg.model';

const TOKEN_EXPIRY_SKEW_SECONDS = 60;

export type StoredAuth0RopgTokens = Auth0RopgTokens & {
  expires_at: number;
};

/**
 * True when the SPA may call the localhost ROPG endpoint.
 * Honored only on a Vite dev server and a loopback hostname.
 */
export const shouldAttemptLocalhostRopg = (options: {
  hostname: string;
  isDev: boolean;
  skip: boolean;
}): boolean => {
  if (!options.isDev || options.skip) return false;
  return isLocalHostname(options.hostname);
};

export const isLocalhostRopgSkipped = (): boolean =>
  window.sessionStorage.getItem(AUTH0_LOCALHOST_ROPG_SKIP_KEY) === 'true';

/** Prevents password grant from undoing logout in this tab. */
export const skipLocalhostRopg = (): void => {
  window.sessionStorage.setItem(AUTH0_LOCALHOST_ROPG_SKIP_KEY, 'true');
};

export const clearStoredLocalhostRopgTokens = (): void => {
  window.localStorage.removeItem(AUTH0_LOCALHOST_ROPG_TOKENS_KEY);
};

export const isStoredAccessTokenFresh = (
  tokens: StoredAuth0RopgTokens,
  nowSeconds = Date.now() / 1000,
): boolean => tokens.expires_at - TOKEN_EXPIRY_SKEW_SECONDS > nowSeconds;

/**
 * Read cached ROPG tokens from localStorage. Returns undefined when the payload
 * is missing or not a token object.
 */
export const readStoredLocalhostRopgTokens = ():
  StoredAuth0RopgTokens | undefined => {
  const raw = window.localStorage.getItem(AUTH0_LOCALHOST_ROPG_TOKENS_KEY);
  if (!raw) return undefined;
  try {
    const parsed: unknown = JSON.parse(raw);
    const tokens = parseAuth0RopgTokenResponse(parsed);
    if (!tokens.ok || !guards.is.object(parsed)) return undefined;
    const expiresAt = parsed.expires_at;
    if (!guards.is.number(expiresAt)) return undefined;
    return { ...tokens.tokens, expires_at: expiresAt };
  } catch {
    return undefined;
  }
};

export const writeStoredLocalhostRopgTokens = (
  tokens: Auth0RopgTokens,
): StoredAuth0RopgTokens => {
  const stored: StoredAuth0RopgTokens = {
    ...tokens,
    expires_at: Date.now() / 1000 + tokens.expires_in,
  };
  window.localStorage.setItem(
    AUTH0_LOCALHOST_ROPG_TOKENS_KEY,
    JSON.stringify(stored),
  );
  return stored;
};

export const localhostRopgUrl = (apiUrl: string): string =>
  `${apiUrl.replace(/\/$/, '')}${AUTH0_LOCALHOST_ROPG_PATH}`;

/**
 * Human-readable reason for a failed localhost ROPG attempt.
 * Used by the localhost ROPG gate so a failed grant is not a stuck spinner.
 */
export const messageFromUnknownError = (error: unknown): string => {
  if (guards.is.error(error) && error.message) return error.message;
  if (guards.is.string(error) && error) return error;
  return 'localhost ROPG failed';
};

export const userFromIdToken = (idToken: string): User | undefined => {
  try {
    const claims = jwtDecode<Record<string, unknown>>(idToken);
    if (!guards.is.string(claims.sub) || !claims.sub) return undefined;
    return {
      sub: claims.sub,
      name: guards.is.string(claims.name) ? claims.name : undefined,
      nickname: guards.is.string(claims.nickname) ? claims.nickname : undefined,
      email: guards.is.string(claims.email) ? claims.email : undefined,
      picture: guards.is.string(claims.picture) ? claims.picture : undefined,
      email_verified: guards.is.boolean(claims.email_verified)
        ? claims.email_verified
        : undefined,
    };
  } catch {
    return undefined;
  }
};

export class LocalhostRopgNotConfiguredError extends Error {
  constructor() {
    super('localhost ROPG is not configured');
    this.name = 'LocalhostRopgNotConfiguredError';
  }
}

/**
 * The localhost API never answered, so Auth0 was not contacted.
 * Distinct from a grant failure, which only happens after the API responds.
 */
export class LocalhostRopgApiUnreachableError extends Error {
  readonly apiUrl: string;

  constructor(apiUrl: string, cause: unknown) {
    super(
      `Start the server at ${apiUrl} and reload. Auth0 was not contacted.`,
      { cause },
    );
    this.name = 'LocalhostRopgApiUnreachableError';
    this.apiUrl = apiUrl;
  }
}

const AUTH0_AUTO_LOGIN_FAILURE_TITLE =
  'Localhost Auth0 auto-login failed. Universal Login was not started.';

export type LocalhostRopgFailureCopy = {
  title: string;
  detail: string;
};

/**
 * Visible copy when localhost auto-login stops.
 * An unreachable API is not an Auth0 failure.
 */
export const localhostRopgFailureCopy = (
  error: unknown,
): LocalhostRopgFailureCopy => {
  if (error instanceof LocalhostRopgApiUnreachableError) {
    return {
      title: 'The API server is not reachable.',
      detail: error.message,
    };
  }
  return {
    title: AUTH0_AUTO_LOGIN_FAILURE_TITLE,
    detail: messageFromUnknownError(error),
  };
};

/**
 * True when the API has no ROPG grant. 404 is the handler. 409 only counts
 * when the body is {@link ErrorMessage.AppVersionMismatch} (unmounted path
 * hitting the version gate on `/`).
 */
export const isLocalhostRopgNotConfiguredResponse = (options: {
  status: number;
  payload?: unknown;
}): boolean => {
  if (options.status === 404) return true;
  if (options.status !== errorStatusCodes[ErrorMessage.AppVersionMismatch]) {
    return false;
  }
  return (
    guards.is.object(options.payload) &&
    options.payload.error === ErrorMessage.AppVersionMismatch
  );
};

let inflight: Promise<Auth0RopgTokens> | undefined;

/**
 * Fetch tokens from the localhost API. Dedupes overlapping calls (React Strict Mode).
 */
export const requestLocalhostRopgTokens = (
  apiUrl: string,
  refreshToken?: string,
): Promise<Auth0RopgTokens> => {
  if (inflight) return inflight;
  inflight = fetchLocalhostRopgTokens(apiUrl, refreshToken).finally(() => {
    inflight = undefined;
  });
  return inflight;
};

const fetchLocalhostRopgTokens = async (
  apiUrl: string,
  refreshToken?: string,
): Promise<Auth0RopgTokens> => {
  let response: Response;
  try {
    response = await fetch(localhostRopgUrl(apiUrl), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'omit',
      body: JSON.stringify(refreshToken ? { refresh_token: refreshToken } : {}),
    });
  } catch (error) {
    // fetch rejects only when no HTTP response arrives (server down, offline).
    throw new LocalhostRopgApiUnreachableError(apiUrl, error);
  }
  if (response.status === 404) {
    throw new LocalhostRopgNotConfiguredError();
  }
  if (!response.ok) {
    const payload: unknown = await response.json().catch(() => undefined);
    if (
      isLocalhostRopgNotConfiguredResponse({
        status: response.status,
        payload,
      })
    ) {
      throw new LocalhostRopgNotConfiguredError();
    }
    const detail =
      guards.is.object(payload) && guards.is.string(payload.error)
        ? payload.error
        : `HTTP ${response.status}`;
    throw new Error(`localhost ROPG failed (${detail})`);
  }
  const body: unknown = await response.json();
  const parsed = parseAuth0RopgTokenResponse(body);
  if (!parsed.ok) {
    throw new Error(parsed.reason);
  }
  return parsed.tokens;
};
